"use client"

import { useRef } from "react"
import { isFilled } from "@prismicio/client"
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next"
import { PrismicRichText } from "@prismicio/react"
import {
  LazyMotion,
  domAnimation,
  m,
  useScroll,
  useTransform,
  type Variants,
} from "motion/react"
import { vFadeIn, vFadeUp, vStaggerContainer } from "@/lib/animations"
import { SiteLogo } from "@/components/site-logo"
import type { LayoutDocument } from "@/prismicio-types"

type FooterClientProps = {
  layout: LayoutDocument["data"]
}

// Floating particles: 8 gold dots scattered across the footer bg
const particles = [
  { size: 4, top: "12%", left: "8%", dur: 5.5, delay: 0 },
  { size: 3, top: "35%", left: "20%", dur: 7.0, delay: 1.2 },
  { size: 5, top: "60%", left: "5%", dur: 6.0, delay: 0.5 },
  { size: 3, top: "80%", left: "30%", dur: 8.0, delay: 2.0 },
  { size: 4, top: "20%", left: "75%", dur: 6.5, delay: 0.8 },
  { size: 3, top: "50%", left: "88%", dur: 7.5, delay: 1.5 },
  { size: 5, top: "70%", left: "65%", dur: 5.0, delay: 0.3 },
  { size: 3, top: "15%", left: "50%", dur: 9.0, delay: 3.0 },
]

const vLinkItem: Variants = {
  hidden: { opacity: 0, x: -12 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.45,
      delay: i * 0.07,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  }),
}

export const FooterClient = ({ layout }: FooterClientProps) => {
  const {
    footer_logo,
    footer_logo_description,
    link_text,
    header_menu_item,
    social_label,
    social_media_link,
    footer_text,
  } = layout

  const footerRef = useRef<HTMLElement>(null)

  const { scrollYProgress } = useScroll({
    target: footerRef,
    offset: ["start end", "end end"],
  })

  // Top shimmer line grows left→right as footer enters view
  const shimmerScaleX = useTransform(scrollYProgress, [0, 0.5], [0, 1])

  // Mandala ring: spin speed driven by scroll (0→720deg over full scroll)
  const ringRotate = useTransform(scrollYProgress, [0, 1], [0, 270])

  return (
    <footer ref={footerRef} className="relative overflow-hidden text-white">
      <LazyMotion features={domAnimation}>
        {/* ── Parallax gradient background ── */}
        <m.div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background:
              "linear-gradient(160deg, oklch(0.21 0.085 22) 0%, oklch(0.28 0.10 25) 40%, oklch(0.32 0.13 35) 70%, oklch(0.26 0.11 40) 100%)",
          }}
        />

        {/* ── Floating gold particles ── */}
        {particles.map((p, i) => (
          <m.div
            key={i}
            aria-hidden
            className="pointer-events-none absolute rounded-full"
            style={{
              width: p.size,
              height: p.size,
              top: p.top,
              left: p.left,
              background: "var(--sacred-gold)",
            }}
            animate={{
              y: [0, -14, 0],
              opacity: [0.25, 0.6, 0.25],
            }}
            transition={{
              duration: p.dur,
              delay: p.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}

        {/* ── Mandala / decorative ring — scroll-driven rotation ── */}
        <m.div
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-1/2"
          style={{ rotate: ringRotate }}
        >
          <svg
            width="440"
            height="440"
            viewBox="0 0 440 440"
            fill="none"
            className="opacity-[0.04]"
          >
            <circle
              cx="220"
              cy="220"
              r="210"
              stroke="white"
              strokeWidth="1"
              strokeDasharray="6 10"
            />
            <circle
              cx="220"
              cy="220"
              r="170"
              stroke="white"
              strokeWidth="0.7"
              strokeDasharray="3 14"
            />
            <circle
              cx="220"
              cy="220"
              r="120"
              stroke="white"
              strokeWidth="1"
              strokeDasharray="8 6"
            />
            {/* 8-petal lotus outline */}
            {Array.from({ length: 8 }).map((_, k) => {
              const angle = (k * 45 * Math.PI) / 180
              const x1 = 220 + Math.cos(angle) * 90
              const y1 = 220 + Math.sin(angle) * 90
              return (
                <line
                  key={k}
                  x1="220"
                  y1="220"
                  x2={x1}
                  y2={y1}
                  stroke="white"
                  strokeWidth="0.6"
                  opacity="0.7"
                />
              )
            })}
          </svg>
        </m.div>

        {/* ── Top shimmer line (grows left→right) ── */}
        <m.div
          aria-hidden
          className="absolute inset-x-0 top-0 h-px origin-left"
          style={{
            scaleX: shimmerScaleX,
            background:
              "linear-gradient(to right, var(--sacred-maroon), var(--sacred-orange), var(--sacred-gold), var(--sacred-orange), var(--sacred-maroon))",
          }}
        />

        {/* ── Main columns ── */}
        <m.div
          variants={vStaggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-40px" }}
          className="relative container flex flex-col justify-between gap-11 section-py sm:flex-row sm:gap-12 md:gap-14 lg:gap-16 xl:gap-20"
        >
          {/* Column 1 — Logo + description */}
          <m.div variants={vFadeIn} className="flex flex-1 flex-col gap-4">
            <SiteLogo
              image={footer_logo}
              title={layout.title}
              subtitle={layout.subtitle}
              variant="footer"
            />

            {isFilled.keyText(footer_logo_description) && (
              <p className="max-w-sm text-white/70">
                {footer_logo_description}
              </p>
            )}
          </m.div>

          <div className="flex flex-col gap-11 sm:flex-row sm:gap-12 md:gap-14 lg:gap-16 xl:gap-20">
            {/* Column 2 — Quick links */}
            <m.div variants={vFadeUp} className="flex flex-col gap-4">
              {isFilled.keyText(link_text) && (
                <p className="text-xs font-semibold tracking-wide text-white/90 uppercase">
                  {link_text}
                </p>
              )}
              {header_menu_item.length > 0 && (
                <ul className="flex list-none flex-col gap-1 pl-0 marker:content-none">
                  {header_menu_item.map((link, idx) => (
                    <m.li
                      key={idx}
                      custom={idx}
                      variants={vLinkItem}
                      className="group relative m-0 flex items-center gap-2"
                    >
                      {/* Animated left dot */}
                      <m.span
                        className="size-1 shrink-0 rounded-full"
                        style={{ background: "var(--sacred-gold)" }}
                        initial={{ scaleX: 0, opacity: 0 }}
                        whileHover={{ scaleX: 1, opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      />
                      <PrismicNextLink
                        field={link}
                        className="relative py-1 text-base text-white/70 no-underline transition-colors duration-200 hover:text-white hover:no-underline md:text-sm"
                      />
                    </m.li>
                  ))}
                </ul>
              )}
            </m.div>

            {/* Column 3 — Social media */}
            <m.div variants={vFadeUp} className="flex flex-col gap-4">
              {isFilled.keyText(social_label) && (
                <p className="text-xs font-semibold tracking-wide text-white/90 uppercase">
                  {social_label}
                </p>
              )}
              {social_media_link.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {social_media_link.map((item, idx) => (
                    <m.div
                      key={idx}
                      className="relative"
                      whileHover="hover"
                      initial="rest"
                    >
                      {/* Pulse ring on hover */}
                      <m.span
                        aria-hidden
                        className="pointer-events-none absolute inset-0 rounded-xl"
                        variants={{
                          rest: { scale: 1, opacity: 0 },
                          hover: { scale: 1.6, opacity: 0 },
                        }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        style={{
                          border: "1px solid var(--sacred-gold)",
                        }}
                      />
                      <PrismicNextLink
                        field={item.link}
                        aria-label={(() => {
                          if (item.link.text) return item.link.text
                          try {
                            const url =
                              "url" in item.link
                                ? (item.link.url as string)
                                : ""
                            const host = new URL(url).hostname.replace(
                              /^www\./,
                              "",
                            )
                            const platform = host.split(".")[0]
                            return (
                              platform.charAt(0).toUpperCase() +
                              platform.slice(1)
                            )
                          } catch {
                            return "Social media link"
                          }
                        })()}
                        className="relative flex size-10 items-center justify-center rounded-xl no-underline transition-colors duration-200 hover:no-underline"
                        style={{
                          background: "oklch(1 0 0 / 0.08)",
                        }}
                      >
                        <m.span
                          className="flex size-full items-center justify-center"
                          variants={{
                            rest: { scale: 1 },
                            hover: { scale: 1.18 },
                          }}
                          transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 15,
                          }}
                        >
                          {isFilled.image(item.icon) && (
                            <PrismicNextImage
                              field={item.icon}
                              width={20}
                              height={20}
                              fallbackAlt=""
                              className="size-5 object-contain"
                              imgixParams={{
                                auto: ["format", "compress"],
                                q: 75,
                              }}
                            />
                          )}
                        </m.span>
                      </PrismicNextLink>
                    </m.div>
                  ))}
                </div>
              )}
            </m.div>
          </div>
        </m.div>

        {/* ── Bottom bar ── */}
        {isFilled.richText(footer_text) && (
          <m.div
            className="relative border-t border-white/10"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }}
          >
            <div className="container flex flex-col gap-2 py-6 text-center sm:py-7 md:py-8 [&_p]:text-sm [&_p]:text-white/50">
              <PrismicRichText field={footer_text} />
            </div>
          </m.div>
        )}
      </LazyMotion>
    </footer>
  )
}
